import config from 'config'

const STORAGE_KEY = 'app_timezone'

let runtimeTimezone: string | null = null

function isValidIanaTimezone(tz: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** Active IANA timezone: tenant override → env/config fallback. */
export function getAppTimezone(): string {
  if (runtimeTimezone && isValidIanaTimezone(runtimeTimezone)) {
    return runtimeTimezone
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && isValidIanaTimezone(stored)) {
      runtimeTimezone = stored
      return stored
    }
  }
  const fallback = config.appTimezone || 'Africa/Cairo'
  return isValidIanaTimezone(fallback) ? fallback : 'UTC'
}

/** Persist tenant timezone for display and calendar helpers. */
export function setAppTimezone(tz: string | null | undefined): void {
  if (!tz || !isValidIanaTimezone(tz)) return
  runtimeTimezone = tz
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, tz)
  }
}

/** @deprecated Prefer {@link getAppTimezone} — kept as a live getter alias for older imports. */
export const APP_TIMEZONE = config.appTimezone

function formatYmdInTz(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/** Calendar «today» in app timezone (YYYY-MM-DD) for date pickers and API date-only fields. */
export function todayLocalDateString(): string {
  return formatYmdInTz(new Date(), getAppTimezone())
}

/** Any instant → YYYY-MM-DD in app timezone. */
export function dateToLocalDateString(date: Date): string {
  return formatYmdInTz(date, getAppTimezone())
}

/**
 * Current wall-clock datetime in app timezone as `Y-m-d H:i:s` (for POS sale_date storage).
 */
export function nowAppTimezoneDateTimeString(date: Date = new Date()): string {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = f.formatToParts(date)
  const g = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '00'
  return `${g('year')}-${g('month')}-${g('day')} ${g('hour')}:${g('minute')}:${g('second')}`
}

/** Minute-precision datetime-local value in the active tenant/app timezone. */
export function dateTimeLocalMinuteInAppTimezone(date: Date = new Date()): string {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  const parts = f.formatToParts(date)
  const g = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '00'
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`
}

function resolveLocale(): string {
  if (typeof localStorage === 'undefined') return 'ar-EG'
  const lng = localStorage.getItem('i18nextLng') || localStorage.getItem('language') || ''
  // Force Latin digits even in Arabic UI (avoid ٠١٢٣…).
  if (lng.startsWith('ar')) return 'ar-EG-u-nu-latn'
  if (lng.startsWith('fr')) return 'fr-FR'
  if (lng.startsWith('en')) return 'en-GB'
  return 'ar-EG-u-nu-latn'
}

/** Full date + time for tables and detail (includes seconds). */
export function formatDateTimeDisplay(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (value == null || value === '') return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(resolveLocale(), {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...options,
  }).format(d)
}

/** Date + time without seconds (common in POS / lists). */
export function formatDateTimeShort(
  value: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (value == null || value === '') return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(resolveLocale(), {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }).format(d)
}

/** Date only in app timezone. */
export function formatDateDisplay(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(resolveLocale(), {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
}

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * DD/MM/YYYY for compact UI labels (filters, range triggers).
 * Avoids Intl bidi control chars that break date order inside RTL paragraphs.
 */
export function formatDateLabelStable(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '—'
  if (typeof value === 'string') {
    const ymd = value.trim()
    const m = YMD_RE.exec(ymd)
    if (m) return `${m[3]}/${m[2]}/${m[1]}`
  }
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'
  const ymd = dateToLocalDateString(d)
  const m = YMD_RE.exec(ymd)
  if (m) return `${m[3]}/${m[2]}/${m[1]}`
  return formatDateDisplay(value).replace(/[\u200E\u200F\u061C]/g, '')
}

function valueHasExplicitTime(value: string): boolean {
  const v = value.trim()
  if (/T\d{2}:\d{2}/.test(v) && !/T00:00(:00)?(\.\d+)?(Z)?$/.test(v)) return true
  if (/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(v) && !/ 00:00:00$/.test(v)) return true
  return false
}

/**
 * Standard formatter for list/table date columns: shows date + time when available,
 * otherwise falls back to `fallback` (e.g. created_at for date-only business fields).
 */
export function formatTableDateTime(
  primary: string | Date | null | undefined,
  fallback?: string | Date | null | undefined,
): string {
  if (primary != null && primary !== '') {
    if (primary instanceof Date || valueHasExplicitTime(String(primary))) {
      return formatDateTimeShort(primary)
    }
  }
  if (fallback != null && fallback !== '') return formatDateTimeShort(fallback)
  if (primary != null && primary !== '') return formatDateDisplay(primary)
  return '—'
}

/**
 * Value for &lt;input type="datetime-local"/&gt; from an ISO string, in app timezone.
 */
export function toDatetimeLocalValue(iso: string | Date | null | undefined): string {
  if (iso == null || iso === '') return ''
  const d = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(d.getTime())) return ''
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = f.formatToParts(d)
  const g = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`
}

function wallClockKeyInAppTz(instantMs: number): string {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: getAppTimezone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const parts = f.formatToParts(new Date(instantMs))
  const g = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`
}

/**
 * Parse a `datetime-local` string as wall-clock time in app timezone and return UTC ISO.
 */
export function datetimeLocalAppTzToIso(local: string | null | undefined): string | null {
  if (local == null || local === '') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local.trim())
  if (!m) return null
  const y = +m[1]
  const mo = +m[2]
  const d = +m[3]
  const h = +m[4]
  const mi = +m[5]
  const targetKey = `${y.toString().padStart(4, '0')}-${mo.toString().padStart(2, '0')}-${d
    .toString()
    .padStart(2, '0')}T${h.toString().padStart(2, '0')}:${mi.toString().padStart(2, '0')}`

  let lo = Date.UTC(y, mo - 1, d, h, mi) - 48 * 3600000
  let hi = Date.UTC(y, mo - 1, d, h, mi) + 48 * 3600000
  for (let i = 0; i < 64; i++) {
    const mid = Math.floor((lo + hi) / 2)
    const key = wallClockKeyInAppTz(mid)
    if (key === targetKey) return new Date(mid).toISOString()
    if (key < targetKey) lo = mid + 1
    else hi = mid - 1
    if (lo > hi) break
  }
  return new Date(Date.UTC(y, mo - 1, d, h, mi)).toISOString()
}
